import { HocuspocusProvider } from "@hocuspocus/provider";
import * as Y from "yjs";
import type { UserInfo } from "../types";

export type RoomScope = "workspace" | "review";

export interface MagicCommentProviderOptions {
  /** Hocuspocus server URL (from MAGIC_COMMENT_SERVER_URL) */
  serverUrl: string;
  /** API key for authentication (from MAGIC_COMMENT_API_KEY) */
  apiKey: string;
  /** Project identifier used as the room namespace */
  projectId: string;
  /** Review identifier for page-level collaboration */
  reviewId?: string;
  /** Normalized page identifier */
  pageId?: string;
  /** Scope of the Yjs room */
  scope?: RoomScope;
  /** Optional explicit room name override */
  roomName?: string;
  /** Current user identity */
  user: UserInfo;
  /** Callback when connection status changes */
  onStatusChange?: (
    status: "connecting" | "connected" | "disconnected",
  ) => void;
}

export interface MagicCommentProvider {
  /** The underlying Yjs document (shared state) */
  doc: Y.Doc;
  /** The Hocuspocus provider (manages WebSocket + awareness) */
  provider: HocuspocusProvider;
  /** Destroy the connection and clean up */
  destroy: () => void;
}

export function getWorkspaceRoomName(projectId: string): string {
  return `${projectId}:workspace`;
}

export function getReviewRoomName(
  projectId: string,
  reviewId: string,
  pageId: string,
): string {
  return `${projectId}:${reviewId}:${pageId}`;
}

export function resolveRoomName(options: MagicCommentProviderOptions): string {
  if (options.roomName) return options.roomName;

  const scope = options.scope ?? "workspace";
  if (scope === "workspace") {
    return getWorkspaceRoomName(options.projectId);
  }

  const reviewId = options.reviewId ?? "default";
  const pageId = options.pageId ?? "index";
  return getReviewRoomName(options.projectId, reviewId, pageId);
}

/**
 * Creates and connects a Hocuspocus provider for multiplayer collaboration.
 * Rooms are derived from project/review/page identity unless an explicit
 * roomName is provided.
 */
export function createMagicCommentProvider(
  options: MagicCommentProviderOptions,
): MagicCommentProvider {
  const { serverUrl, apiKey, user, onStatusChange } = options;
  const roomName = resolveRoomName(options);

  const doc = new Y.Doc();

  const provider = new HocuspocusProvider({
    url: serverUrl,
    name: roomName,
    document: doc,
    token: apiKey,
    onStatus({ status }) {
      onStatusChange?.(status as "connecting" | "connected" | "disconnected");
    },
  });

  // Set initial awareness state with user info
  provider.setAwarenessField("user", user);
  provider.setAwarenessField("cursor", null);
  provider.setAwarenessField("selectedFrameId", null);

  return {
    doc,
    provider,
    destroy() {
      provider.destroy();
      doc.destroy();
    },
  };
}
